// ── DE §23-Spekulationsfrist: Datums-Helfer (1-Jahres-Frist) ────────────────
//
// EINE Quelle für die DEUTSCHE Haltefrist-ANZEIGE. Spiegelt exakt die Regel der
// echten Steuer-Engine (`fifo.js isWithinSpekulationsfrist`, BGB §188 Abs. 2):
// die Frist endet MIT Ablauf des Jahrestags — ein Lot ist erst AB dem Folgetag
// des Jahrestags außerhalb der Frist ("frei"). Beispiel: Kauf 2024-01-15 →
// frei ab 2025-01-16 (nicht 2025-01-15).
//
// Vorher rechneten Anzeige (summarizeLots/TaxTimeline) und PDF mit „rollend
// ≥ 365 Tage". Das wich am Rand um einen Tag von der Engine + den Alerts ab
// (und über Schaltjahre hinweg). Diese Datei vereinheitlicht die Anzeige auf
// die Engine-Regel.
//
// WICHTIG (DE vs. AT): Dies ist die DEUTSCHE Frist. Österreich hat eine eigene,
// rechtlich getrennte Altvermögen-Frist in `austrianCost.js` (`withinAltFrist`,
// Käufe vor 2021-03-01) — diese Datei NICHT mit der AT-Logik zusammenführen,
// auch wenn beide nominal „1 Jahr" sind. Genutzt nur im DE-Anzeige-Pfad
// (Smart Check / TaxTimeline / PDF-Open-Lots / Alert-Snapshot).
//
// Reine ES-Module-Datei — KEINE Browser-/Firebase-Deps (wird auch server-seitig
// von api/_lib/pdfBuilder.js importiert, gleicher Purity-Vertrag wie taxRules.js).

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Datum (YYYY-MM-DD), ab dem ein Lot außerhalb der 1-Jahres-Frist ist.
 * = Jahrestag der Anschaffung + 1 Tag.
 * @param {string} buyDateStr  Kaufdatum (YYYY-MM-DD oder ISO)
 * @returns {string|null}      Frei-Datum oder null bei ungültigem Datum
 */
export function lotFreeDate(buyDateStr) {
  if (!buyDateStr) return null;
  const buy = new Date(buyDateStr);
  if (isNaN(buy.getTime())) return null;
  const free = new Date(Date.UTC(buy.getUTCFullYear() + 1, buy.getUTCMonth(), buy.getUTCDate()));
  free.setUTCDate(free.getUTCDate() + 1); // frei ab Folgetag des Jahrestags (BGB §188 II)
  return free.toISOString().slice(0, 10);
}

function todayStrOf(today) {
  const d = today instanceof Date ? today : new Date(today);
  return d.toISOString().slice(0, 10);
}

/**
 * Ist das Lot heute (oder am Stichtag `today`) außerhalb der Frist?
 * @param {string} buyDateStr
 * @param {Date|number|string} [today=new Date()]
 * @returns {boolean}
 */
export function isLotFree(buyDateStr, today = new Date()) {
  const fd = lotFreeDate(buyDateStr);
  if (!fd) return false;
  return todayStrOf(today) >= fd;
}

/**
 * Tage bis das Lot frei wird (0 wenn bereits frei). null bei ungültigem Datum.
 * @param {string} buyDateStr
 * @param {Date|number|string} [today=new Date()]
 * @returns {number|null}
 */
export function daysUntilFree(buyDateStr, today = new Date()) {
  const fd = lotFreeDate(buyDateStr);
  if (!fd) return null;
  const todayMs = +(today instanceof Date ? today : new Date(today));
  const freeMs = Date.parse(`${fd}T00:00:00Z`);
  return Math.max(0, Math.ceil((freeMs - todayMs) / DAY_MS));
}

/**
 * Millisekunden-Zeitpunkt (UTC-Mitternacht) des Frei-Datums. Für Chart-Geometrie.
 * @param {string} buyDateStr
 * @returns {number|null}
 */
export function lotFreeMs(buyDateStr) {
  const fd = lotFreeDate(buyDateStr);
  return fd ? Date.parse(`${fd}T00:00:00Z`) : null;
}
